import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMolds } from '../hooks/useMolds';
import MoldListSimplified from '../components/MoldList/MoldListSimplified';
import MainContainer from '../components/layout/MainContainer';
import MoldListSkeleton from '../components/MoldList/MoldListSkeleton';
import ErrorPage from '../components/pages/Error';
import { StatusFilterType } from '../components/MoldList/types';
import { usePreferences } from '../context/PreferencesContext';
import { ROUTES } from '../constants/routes';
import { useAppState } from '../context/AppStateContext';
import { getMoldSearchFields, getDefaultMoldSearchField } from '../components/KanbanBoard/utils/columnConfigurations';

const MoldListContainer: React.FC = () => {
  const { molds, loading, error, fetchMolds } = useMolds();
  const { preferences } = usePreferences();
  const { companyCode } = useAppState();
  const location = useLocation();
  const navigate = useNavigate();

  // Get company-specific search fields
  const moldSearchFields = getMoldSearchFields(companyCode);
  const defaultSearchField = getDefaultMoldSearchField(companyCode);

  // Search state
  const queryParams = new URLSearchParams(location.search);
  const [searchTerm, setSearchTerm] = useState(queryParams.get('q') ?? '');
  const [selectedSearchField, setSelectedSearchField] = useState(queryParams.get('qField') || defaultSearchField);
  const initialStatusFilters = (queryParams.get('status')?.split(',').filter(Boolean) as StatusFilterType[] | undefined) || ['ALL'];
  const initialCustomerFilter = queryParams.get('customer') || 'ALL';

  const updateQuery = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(location.search);
    Object.entries(updates).forEach(([key, value]) => value && value !== 'ALL' ? next.set(key, value) : next.delete(key));
    navigate({ search: next.toString() }, { replace: true });
  };

  useEffect(() => {
    fetchMolds();
  }, [fetchMolds]);

  const handleSearch = (query: string, field: string) => {
    setSearchTerm(query);
    setSelectedSearchField(field);
    updateQuery({ q: query, qField: query ? field : undefined });
  };

  const handleSearchFieldChange = (field: string) => {
    setSelectedSearchField(field);
    updateQuery({ qField: searchTerm ? field : undefined });
  };

  const handleCreateNewMold = () => {
    navigate(ROUTES.CREATE_NEW_MOLD);
  };

  if (loading) {
    return (
      <MainContainer 
        enableSearch={true}
        searchFields={moldSearchFields}
        onSearch={handleSearch}
        onSearchFieldChange={handleSearchFieldChange}
        initialSearchQuery={searchTerm}
        initialSearchField={selectedSearchField}
        enableScan={true}
        primaryAction={{
          label: 'เพิ่มแม่พิมพ์',
          onClick: handleCreateNewMold,
          variant: 'primary'
        }}
      >
        <MoldListSkeleton viewMode="card" />
      </MainContainer>
    );
  }

  if (error) return <ErrorPage />;

  return (
    <MainContainer
      title='ทะเบียนแม่พิมพ์'
      enableSearch={true}
      searchFields={moldSearchFields}
      onSearch={handleSearch}
      onSearchFieldChange={handleSearchFieldChange}
      initialSearchQuery={searchTerm}
      initialSearchField={selectedSearchField}
      enableScan={true}
      primaryAction={{
        label: 'เพิ่มแม่พิมพ์',
        onClick: handleCreateNewMold,
        variant: 'primary'
      }}
    >
      <MoldListSimplified
        molds={molds}
        initialStatusFilters={initialStatusFilters}
        initialCustomerFilter={initialCustomerFilter}
        onStatusFilterChange={(statuses) => updateQuery({ status: statuses.length === 1 ? statuses[0] : statuses.join(',') })}
        onCustomerFilterChange={(customer) => updateQuery({ customer })}
        preferences={preferences}
        searchTerm={searchTerm}
        selectedSearchField={selectedSearchField}
      />
    </MainContainer>
  );
};

export default MoldListContainer;
